import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";

export default function Invoice() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/orders/${orderId}`).then((r) => setD(r.data)); }, [orderId]);
  if (!d) return null;
  const { order: o, customer: c, settings: s } = d;
  const igst = c?.state && s.state && c.state.toLowerCase() !== s.state.toLowerCase();
  return (
    <div className="min-h-screen bg-[#FBF9F4] py-8 px-4 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl no-print mb-4 flex justify-between">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="invoice-back"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => window.print()} data-testid="invoice-print"><Printer className="mr-1 h-4 w-4" />Print / PDF</Button>
      </div>
      <div className="mx-auto max-w-3xl rounded-2xl border border-[#E7E2D6] bg-white p-8 sm:p-12 print:border-0" data-testid="invoice-doc">
        <div className="flex justify-between gap-6">
          <div>
            <div className="font-display text-2xl font-extrabold text-[#0F382C]">{s.business_name}</div>
            <div className="text-sm text-muted-foreground whitespace-pre-line">{s.address}</div>
            {s.phone && <div className="text-sm text-muted-foreground">Ph: {s.phone}</div>}
            {s.gstin && <div className="text-sm font-semibold mt-1">GSTIN: {s.gstin}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D96B43]">{s.gstin ? "Tax Invoice" : "Bill of Supply"}</div>
            <div className="font-mono text-lg font-semibold mt-1" data-testid="invoice-number">{o.invoice_no || "Draft"}</div>
            <div className="text-sm text-muted-foreground">{fmtDate(o.date)}</div>
          </div>
        </div>
        <div className="mt-8 rounded-xl bg-[#FAF4E8] p-4 text-sm">
          <div className="text-xs uppercase text-muted-foreground">Bill to</div>
          <div className="font-semibold">{o.customer_name}</div>
          <div>{o.address}</div><div>{o.customer_phone}</div>
          {c?.gstin && <div className="font-semibold" data-testid="invoice-customer-gstin">GSTIN: {c.gstin}</div>}
        </div>
        <table className="mt-8 w-full text-sm">
          <thead><tr className="border-b-2 border-[#0F382C] text-left"><th className="py-2">Item</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">GST</th><th className="text-right">Amount</th></tr></thead>
          <tbody>
            {o.items.map((i) => (
              <tr key={i.product_id} className="border-b border-[#E7E2D6]"><td className="py-2">{i.name}<div className="text-xs text-muted-foreground">{i.unit_label}{i.hsn ? ` · HSN ${i.hsn}` : ""}</div></td><td className="text-right font-mono">{i.qty}</td><td className="text-right font-mono">{inr(i.price)}</td><td className="text-right font-mono">{i.gst_rate}%</td><td className="text-right font-mono">{inr(i.amount)}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="mt-6 ml-auto w-full max-w-xs space-y-1 text-sm font-mono">
          <div className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></div>
          {igst ? <div className="flex justify-between"><span>IGST</span><span>{inr(o.tax)}</span></div> : (
            <><div className="flex justify-between"><span>CGST</span><span>{inr(o.tax / 2)}</span></div><div className="flex justify-between"><span>SGST</span><span>{inr(o.tax / 2)}</span></div></>
          )}
          <div className="flex justify-between border-t-2 border-[#0F382C] pt-2 text-base font-semibold" data-testid="invoice-total"><span>Total</span><span>{inr(o.total)}</span></div>
          {o.cash_collected > 0 && <div className="flex justify-between text-emerald-700"><span>Paid on delivery</span><span>{inr(o.cash_collected)}</span></div>}
        </div>
        <div className="mt-12 text-xs text-muted-foreground">Delivered {o.delivered_at ? new Date(o.delivered_at).toLocaleString("en-IN") : "-"} by {o.delivered_by || o.delivery_boy_name || "-"}. Thank you for your business.</div>
      </div>
    </div>
  );
}
